import React, { useState, useEffect } from 'react';
import { useDrop, DropTargetMonitor } from 'react-dnd';
import { timetableApi } from '../services/api';
import TimetableSlotEditModal from './TimetableSlotEditModal';
import type { Class, TimetableSlot } from '../types';

// Helper function to format display values
const formatDisplayValue = (value: any): string => {
  if (value === null || value === undefined || value === '' || 
      (typeof value === 'number' && isNaN(value)) || 
      (typeof value === 'string' && (value.toLowerCase() === 'nan' || value === 'null'))) {
    return '-';
  }
  return value.toString();
};

interface TimetableGridProps {
  classes: Class[];
  selectedGrade: number | null;
  selectedTeacher?: string;
  selectedClassCode?: string;
  onSlotUpdate: () => void;
  onLineDrop?: (line: any, day: number, period: number) => void;
}

/* Draggable class component - currently unused
const DraggableClass: React.FC<{ classItem: Class; allocation: ClassAllocation }> = ({ classItem, allocation }) => {
  const [{ isDragging }, drag] = useDrag(() => ({
    type: 'class',
    item: { classItem },
    collect: (monitor: DragSourceMonitor) => ({
      isDragging: monitor.isDragging(),
    }),
  }));

  const isFullyAllocated = allocation.remaining_periods === 0;
  const progressPercentage = (allocation.allocated_periods / allocation.total_periods) * 100;

  return (
    <div
      ref={drag}
      className={`p-1.5 rounded text-xs border cursor-move transition-all duration-200 ${
        isDragging ? 'opacity-50 scale-95' : 'opacity-100 scale-100'
      } ${
        isFullyAllocated 
          ? 'bg-green-100 border-green-300 text-green-800' 
          : 'bg-white border-neutral-silver text-neutral-dark-gray hover:bg-neutral-light-gray'
      }`}
      style={{ opacity: isDragging ? 0.5 : 1 }}
    >
      <div className="font-montserrat font-semibold text-xs">{classItem.code}</div>
      <div className="text-[10px] text-neutral-medium-gray">Grade {classItem.grade}</div>
      <div className="text-[10px] text-neutral-medium-gray">{formatDisplayValue(classItem.teacher1)}</div>
      <div className="mt-1">
        <div className="flex justify-between text-xs">
          <span>{allocation.allocated_periods}/{allocation.total_periods}</span>
          <span>{Math.round(progressPercentage)}%</span>
        </div>
        <div className="w-full bg-neutral-light-gray rounded-full h-1.5 mt-1">
          <div 
            className={`h-1.5 rounded-full transition-all duration-300 ${
              isFullyAllocated ? 'bg-green-500' : 'bg-accent-teal'
            }`}
            style={{ width: `${progressPercentage}%` }}
          ></div>
        </div>
      </div>
    </div>
  );
};
*/

// Grid slot component
const GridSlot: React.FC<{ 
  day: number; 
  period: number; 
  slots: TimetableSlot[];
  onSlotUpdate: () => void;
  onLineDrop?: (line: any, day: number, period: number) => void;
}> = ({ day, period, slots, onSlotUpdate, onLineDrop }) => {
  const [clashInfo, setClashInfo] = useState<any>(null);
  const [isHovering, setIsHovering] = useState(false);
  const [expandedLineGroups, setExpandedLineGroups] = useState<Set<string>>(new Set());
  const [editingSlot, setEditingSlot] = useState<TimetableSlot | null>(null);

  const toggleLineGroup = (lineCode: string) => {
    setExpandedLineGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(lineCode)) {
        newSet.delete(lineCode);
      } else {
        newSet.add(lineCode);
      }
      return newSet;
    });
  };
  
  // Helper function to group slots by their line
  const groupSlotsByLine = (slots: TimetableSlot[]) => {
    const lineGroups: { [lineCode: string]: TimetableSlot[] } = {};
    const individualSlots: TimetableSlot[] = [];
    
    slots.forEach(slot => {
      // Check if the slot has a line field directly
      if (slot.line && formatDisplayValue(slot.line) !== '-') {
        // This slot is part of a line
        const lineCode = slot.line;
        if (!lineGroups[lineCode]) {
          lineGroups[lineCode] = [];
        }
        lineGroups[lineCode].push(slot);
      } else {
        // This slot is individual
        individualSlots.push(slot);
      }
    });
    
    return { lineGroups, individualSlots };
  };
  
  const { lineGroups, individualSlots } = groupSlotsByLine(slots);
  
  const [{ isOver, canDrop }, drop] = useDrop(() => ({
    accept: ['class', 'line'],
    hover: async (item: { classItem?: Class; lineData?: any }) => {
      if (!isHovering && item.classItem) {
        setIsHovering(true);
        try {
          // Use new unified clash checking API
          const response = await timetableApi.checkClashUnified({
            day,
            period,
            code: item.classItem.code,
            class_code: item.classItem.class_code || '',
            teacher1: item.classItem.teacher1,
            teacher2: item.classItem.teacher2 || undefined,
            type: 'class'
          });
          setClashInfo(response.data);
        } catch (error) {
          console.warn('Clash check failed, continuing without clash info:', error);
          setClashInfo(null); // Fail silently, don't block functionality
        }
      }
    },
    drop: async (item: { classItem?: Class; lineData?: any }) => {
      setIsHovering(false);
      setClashInfo(null);
      
      // Handle line drops
      if (item.lineData && onLineDrop) {
        onLineDrop(item.lineData, day, period);
        return;
      }
      
      // Handle class drops (existing logic)
      if (!item.classItem) return;
      
      // Check for clashes before dropping (with fallback)
      let proceedWithCreation = true;
      
      try {
        // Use new unified clash checking API
        const clashResponse = await timetableApi.checkClashUnified({
          day,
          period,
          code: item.classItem.code,
          class_code: item.classItem.class_code || '',
          teacher1: item.classItem.teacher1,
          teacher2: item.classItem.teacher2 || undefined,
          type: 'class'
        });
        const clashData = clashResponse.data;
        
        if (clashData.has_clash) {
          const clashMessages = clashData.clash_details.join('\n');
          proceedWithCreation = window.confirm(
            `⚠️ CLASH DETECTED ⚠️\n\n${clashMessages}\n\nAre you sure you want to proceed with this allocation?`
          );
        }
      } catch (error) {
        console.warn('Clash check failed, proceeding with slot creation:', error);
        // Continue with slot creation even if clash check fails
      }
      
      if (!proceedWithCreation) {
        return; // User cancelled due to clash
      }
      
      // Proceed with slot creation
      try {
        // Determine type based on class structure
        const isLineSubject = item.classItem.subject_section && 
          item.classItem.subject_section !== 'nan' && 
          item.classItem.subject_section !== item.classItem.class_code;
        
        const slotData = {
          day,
          period,
          code: item.classItem.code,  // Foreign key to classes.code
          type: isLineSubject ? 'line' : 'class',  // Determine type dynamically
          class_code: item.classItem.class_code,  // Actual class code for clash detection
          subject_section: item.classItem.subject_section,  // For grouped subjects
          grade: item.classItem.grade,
          teacher1_code: item.classItem.teacher1,
          teacher2_code: item.classItem.teacher2,
          subject_code: item.classItem.subject_code,
          period_length: 1
        };
        
        console.log('Creating slot with data:', JSON.stringify(slotData, null, 2));
        const response = await timetableApi.createSlot(slotData);
        console.log('Slot created successfully:', response.data);
        await onSlotUpdate();
      } catch (error: any) {
        console.error('Error creating slot:', error);
        console.error('Error response:', error.response?.data);
        console.error('Error status:', error.response?.status);
        console.error('Error message:', error.message);
        
        let errorMessage = 'Unknown error creating slot';
        if (error.response?.data?.detail) {
          errorMessage = error.response.data.detail;
        } else if (error.response?.data) {
          errorMessage = JSON.stringify(error.response.data);
        } else if (error.message) {
          errorMessage = error.message;
        }
        
        alert(`Error creating slot: ${errorMessage}`);
      }
    },
    collect: (monitor: DropTargetMonitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  }));
  
  // Reset clash info when not hovering
  React.useEffect(() => {
    if (!isOver) {
      setIsHovering(false);
      setClashInfo(null);
    }
  }, [isOver]);

  const handleRemoveSlot = async (slotId: number) => {
    try {
      console.log('Attempting to delete slot ID:', slotId);
      const response = await timetableApi.deleteSlot(slotId);
      console.log('Delete response:', response);
      
      // Check if response indicates success
      if (response.data && (response.data.success || response.data.message)) {
        console.log('Slot deleted successfully');
        await onSlotUpdate();
      } else {
        throw new Error('Delete operation failed - no success indicator');
      }
    } catch (error: any) {
      console.error('Error removing slot:', error);
      if (error.response) {
        console.error('Error status:', error.response.status);
        console.error('Error data:', error.response.data);
        if (error.response.status === 404) {
          alert('Slot not found - it may have already been deleted');
        } else {
          alert(`Error removing slot: ${error.response.data.detail || error.response.statusText}`);
        }
      } else {
        console.error('Network or other error:', error.message);
        alert(`Error removing slot: ${error.message || 'Unknown error'}`);
      }
    }
  };

  const handleRemoveLineGroup = async (classCode: string) => {
    try {
      console.log('Attempting to delete line group for class:', classCode);
      const response = await timetableApi.deleteLineGroup(day, period, classCode);
      console.log('Line group delete response:', response);
      
      if (response.data && response.data.success) {
        console.log(`Line group deleted successfully. Deleted ${response.data.deleted_count} slots`);
        await onSlotUpdate();
      } else {
        throw new Error('Line group delete operation failed');
      }
    } catch (error: any) {
      console.error('Error removing line group:', error);
      if (error.response) {
        console.error('Error status:', error.response.status);
        console.error('Error data:', error.response.data);
        alert(`Error removing line group: ${error.response.data.detail || error.response.statusText}`);
      } else {
        alert(`Error removing line group: ${error.message || 'Unknown error'}`);
      }
    }
  };

  const getGradeColor = (grade: number) => {
    const colors: { [key: number]: string } = {
      8: 'bg-blue-100 border-blue-300 text-blue-800',
      9: 'bg-green-100 border-green-300 text-green-800',
      10: 'bg-yellow-100 border-yellow-300 text-yellow-800',
      11: 'bg-purple-100 border-purple-300 text-purple-800',
      12: 'bg-red-100 border-red-300 text-red-800',
    };
    return colors[grade] || 'bg-gray-100 border-gray-300 text-gray-800';
  };

  const getGradeLineGroupColors = (grade: number) => {
    const colors: { [key: number]: { border: string; bg: string; text: string; hoverBg: string } } = {
      8: { border: 'border-blue-400', bg: 'bg-blue-50', text: 'text-blue-800', hoverBg: 'hover:bg-blue-100' },
      9: { border: 'border-green-400', bg: 'bg-green-50', text: 'text-green-800', hoverBg: 'hover:bg-green-100' },
      10: { border: 'border-yellow-400', bg: 'bg-yellow-50', text: 'text-yellow-800', hoverBg: 'hover:bg-yellow-100' },
      11: { border: 'border-purple-400', bg: 'bg-purple-50', text: 'text-purple-800', hoverBg: 'hover:bg-purple-100' },
      12: { border: 'border-red-400', bg: 'bg-red-50', text: 'text-red-800', hoverBg: 'hover:bg-red-100' },
    };
    return colors[grade] || { border: 'border-gray-400', bg: 'bg-gray-50', text: 'text-gray-800', hoverBg: 'hover:bg-gray-100' };
  };
  
  // Determine background color based on state
  const getBackgroundColor = () => {
    if (clashInfo?.has_clash && isOver) {
      return 'border-orange-400 bg-orange-100'; // Orange warning for clashes
    }
    if (isOver && canDrop && !clashInfo?.has_clash) {
      return 'border-accent-teal bg-blue-50'; // Normal drop zone
    }
    if (isOver) {
      return 'border-red-400 bg-red-50'; // Invalid drop
    }
    return 'border-neutral-silver bg-neutral-light-gray'; // Default
  };

  return (
    <div
      ref={drop}
      className={`min-h-[60px] p-0.5 border-2 border-dashed transition-all duration-200 relative ${
        getBackgroundColor()
      }`}
    >
      {/* Clash warning overlay */}
      {clashInfo?.has_clash && isOver && (
        <div className="absolute top-0 left-0 right-0 bg-orange-500 text-white text-xs p-1 z-10 rounded-t">
          ⚠️ CLASH DETECTED
        </div>
      )}
      
      <div className="space-y-0.5 mt-2">
        {/* Render line groups with dotted borders */}
        {Object.entries(lineGroups).map(([lineCode, lineSlots]) => {
          const lineGrade = lineSlots[0]?.grade || 10; // Default to grade 10 if no grade found
          const gradeColors = getGradeLineGroupColors(lineGrade);
          
          return (
            <div
              key={`line-${lineCode}`}
              className={`group relative border-2 border-dashed ${gradeColors.border} ${gradeColors.bg} rounded p-1 mb-1`}
            >
              {/* Delete button positioned in top-right corner */}
              <button
                onClick={() => handleRemoveLineGroup(lineSlots[0].code)}
                className="absolute top-0 right-0 w-4 h-4 bg-red-500 text-white rounded-full text-xs flex items-center justify-center transform translate-x-1 -translate-y-1 hover:bg-red-600 opacity-0 group-hover:opacity-100 transition-all duration-200"
                title="Remove entire line group"
              >
                ×
              </button>
              
              {/* Line group header */}
              <div className="mb-1">
                <div className="flex items-center justify-between">
                  <div className={`text-xs font-semibold ${gradeColors.text} flex-1 overflow-hidden`}>
                    <span className="block whitespace-nowrap overflow-hidden text-ellipsis" title={lineCode}>
                      {lineCode}
                    </span>
                  </div>
                  <button
                    onClick={() => toggleLineGroup(lineCode)}
                    className={`${gradeColors.text} transition-colors p-0.5 rounded ${gradeColors.hoverBg} flex-shrink-0 ml-1`}
                    title={expandedLineGroups.has(lineCode) ? 'Collapse classes' : 'Expand classes'}
                  >
                    <svg 
                      className={`w-3 h-3 transition-transform duration-200 ${
                        expandedLineGroups.has(lineCode) ? 'transform rotate-180' : ''
                      }`} 
                      fill="currentColor" 
                      viewBox="0 0 20 20"
                    >
                      <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>
              </div>
              
              {/* Line group slots - conditionally visible */}
              {expandedLineGroups.has(lineCode) && (
              <div className="space-y-0.5">
                {lineSlots.map((slot) => (
                <div
                  key={slot.id}
                  className={`relative p-2 rounded text-xs border ${getGradeColor(slot.grade)} ${
                    slot.has_teacher_clash || slot.has_room_clash || slot.has_class_code_clash ? 'ring-2 ring-red-400' : ''
                  } group cursor-pointer hover:shadow-lg transition-all duration-200`}
                  onClick={() => setEditingSlot(slot)}
                >
                  {slot.type !== 'line' && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveSlot(slot.id);
                      }}
                      className="absolute top-0 right-0 w-4 h-4 bg-red-500 text-white rounded-full text-xs flex items-center justify-center transform translate-x-1 -translate-y-1 hover:bg-red-600 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10"
                      title="Remove individual class"
                    >
                      ×
                    </button>
                  )}
                  
                  {/* Main display - show only the code */}
                  <div className="font-montserrat font-semibold text-center">
                    {slot.code}
                  </div>
                  
                  {/* Hover tooltip - detailed information */}
                  <div className="absolute z-50 left-0 top-full mt-1 p-2 bg-gray-900 text-white text-xs rounded shadow-lg min-w-max opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                    <div className="font-semibold">{slot.code}</div>
                    <div>Type: {slot.type}</div>
                    <div>Class Code: {slot.class_code && slot.class_code.toLowerCase() !== 'nan' ? slot.class_code : 'N/A'}</div>
                    <div>Subject: {formatDisplayValue(slot.subject_code)}</div>
                    <div>Room: {formatDisplayValue(slot.room)}</div>
                    <div>Teacher: {formatDisplayValue(slot.teacher1_code)}</div>
                    {(slot.has_teacher_clash || slot.has_room_clash || slot.has_class_code_clash) && (
                      <div className="text-red-400 font-bold mt-1">
                        ⚠️ {slot.has_teacher_clash && 'Teacher clash'}{slot.has_teacher_clash && (slot.has_room_clash || slot.has_class_code_clash) && ', '}{slot.has_room_clash && 'Room clash'}{slot.has_room_clash && slot.has_class_code_clash && ', '}{slot.has_class_code_clash && 'Class code clash'}
                      </div>
                    )}
                  </div>
                  
                  {/* Small clash indicator */}
                  {(slot.has_teacher_clash || slot.has_room_clash || slot.has_class_code_clash) && (
                    <div className="absolute top-0 left-0 w-2 h-2 bg-red-500 rounded-full transform -translate-x-0.5 -translate-y-0.5"></div>
                  )}
                </div>
              ))}
                </div>
              )}
            </div>
          );
        })}
        
        {/* Render individual slots */}
        {individualSlots.map((slot) => (
          <div
            key={slot.id}
            className={`relative p-1 rounded text-[10px] border ${getGradeColor(slot.grade)} ${
              slot.has_teacher_clash || slot.has_room_clash || slot.has_class_code_clash ? 'ring-2 ring-red-400' : ''
            } group cursor-pointer hover:shadow-lg transition-all duration-200`}
            onClick={() => setEditingSlot(slot)}
          >
            {slot.type !== 'line' && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemoveSlot(slot.id);
                }}
                className="absolute top-0 right-0 w-4 h-4 bg-red-500 text-white rounded-full text-xs flex items-center justify-center transform translate-x-1 -translate-y-1 hover:bg-red-600 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10"
                title="Remove class"
              >
                ×
              </button>
            )}
            
            {/* Main display - show only the code */}
            <div className="font-montserrat font-semibold text-center">
              {slot.code}
            </div>
            
            {/* Hover tooltip - detailed information */}
            <div className="absolute z-50 left-0 top-full mt-1 p-2 bg-gray-900 text-white text-xs rounded shadow-lg min-w-max opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
              <div className="font-semibold">{slot.code}</div>
              <div>Type: {slot.type}</div>
              <div>Class Code: {slot.class_code && slot.class_code.toLowerCase() !== 'nan' ? slot.class_code : 'N/A'}</div>
              <div>Subject: {formatDisplayValue(slot.subject_code)}</div>
              <div>Room: {formatDisplayValue(slot.room)}</div>
              <div>Teacher: {formatDisplayValue(slot.teacher1_code)}</div>
              {(slot.has_teacher_clash || slot.has_room_clash || slot.has_class_code_clash) && (
                <div className="text-red-400 font-bold mt-1">
                  ⚠️ {slot.has_teacher_clash && 'Teacher clash'}{slot.has_teacher_clash && (slot.has_room_clash || slot.has_class_code_clash) && ', '}{slot.has_room_clash && 'Room clash'}{slot.has_room_clash && slot.has_class_code_clash && ', '}{slot.has_class_code_clash && 'Class code clash'}
                </div>
              )}
            </div>
            
            {/* Small clash indicator */}
            {(slot.has_teacher_clash || slot.has_room_clash || slot.has_class_code_clash) && (
              <div className="absolute top-0 left-0 w-2 h-2 bg-red-500 rounded-full transform -translate-x-0.5 -translate-y-0.5"></div>
            )}
          </div>
        ))}
      </div>
      
      {/* Edit Modal */}
      {editingSlot && (
        <TimetableSlotEditModal
          slot={editingSlot}
          onClose={() => setEditingSlot(null)}
          onUpdate={() => {
            setEditingSlot(null);
            onSlotUpdate();
          }}
        />
      )}
    </div>
  );
};

const TimetableGrid: React.FC<TimetableGridProps> = ({ classes, selectedGrade, selectedTeacher, selectedClassCode, onSlotUpdate, onLineDrop }) => {
  const [currentTimetable, setCurrentTimetable] = useState<TimetableSlot[]>([]);
  const [loading, setLoading] = useState(true);

  const days = Array.from({ length: 10 }, (_, i) => i + 1);
  const periods = Array.from({ length: 6 }, (_, i) => i + 1);

  const loadTimetable = async () => {
    try {
      console.log('Loading timetable...');
      const response = await timetableApi.getCurrentTimetable();
      console.log('Timetable loaded:', response.data);
      setCurrentTimetable(response.data);
    } catch (error) {
      console.error('Error loading timetable:', error);
    }
  };

  const handleSlotUpdate = async () => {
    await loadTimetable();
    onSlotUpdate();
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await loadTimetable();
      setLoading(false);
    };
    
    if (classes.length > 0) {
      loadData();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classes]);

  const getSlotsByPosition = (day: number, period: number): TimetableSlot[] => {
    // Hard-coded mapping: base class -> special line codes
    const BASE_CLASS_TO_SPECIAL_LINES: { [key: string]: string[] } = {
      '8A': ['(8) 1. AfrFAL/ZuluFAL ABCD'],
      '8B': ['(8) 1. AfrFAL/ZuluFAL ABCD'],
      '8C': ['(8) 1. AfrFAL/ZuluFAL ABCD'],
      '8D': ['(8) 1. AfrFAL/ZuluFAL ABCD'],
      '8E': ['(8) 2. AfrFAL/ZuluFAL EFG'],
      '8F': ['(8) 2. AfrFAL/ZuluFAL EFG'],
      '8G': ['(8) 2. AfrFAL/ZuluFAL EFG'],
      '8H': ['(8) 3.AfrFAL HI'],
      '8I': ['(8) 3.AfrFAL HI'],
      '9A': ['(9) 1. AfrFAL/ZuluFAL/ZuluH ABCD'],
      '9B': ['(9) 1. AfrFAL/ZuluFAL/ZuluH ABCD'],
      '9C': ['(9) 1. AfrFAL/ZuluFAL/ZuluH ABCD'],
      '9D': ['(9) 1. AfrFAL/ZuluFAL/ZuluH ABCD'],
      '9E': ['(9) 2. AfrFAL/ZuluFAL EFG'],
      '9F': ['(9) 2. AfrFAL/ZuluFAL EFG'],
      '9G': ['(9) 2. AfrFAL/ZuluFAL EFG'],
      '9H': ['(9) 3. AfrFAL HI'],
      '9I': ['(9) 3. AfrFAL HI']
    };

    return currentTimetable.filter(slot => {
      const dayPeriodMatch = slot.day === day && slot.period === period;
      const gradeMatch = !selectedGrade || slot.grade === selectedGrade;
      const teacherMatch = !selectedTeacher || slot.teacher1_code === selectedTeacher || slot.teacher2_code === selectedTeacher;
      
      // Class code filter with special line support
      let classCodeMatch = true;
      if (selectedClassCode) {
        // Match if slot's class_code matches selected
        const directMatch = slot.class_code === selectedClassCode;
        
        // Also match if slot belongs to a special line that contains the selected class code
        const specialLines = BASE_CLASS_TO_SPECIAL_LINES[selectedClassCode] || [];
        const specialLineMatch = specialLines.includes(slot.line || '');
        
        classCodeMatch = directMatch || specialLineMatch;
      }
      
      return dayPeriodMatch && gradeMatch && teacherMatch && classCodeMatch;
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-northwood-navy"></div>
      </div>
    );
  }

  return (
    <div className="card overflow-auto">
      <h3 className="text-h3 font-montserrat text-northwood-navy mb-4">
        Timetable Grid (10-Day Cycle)
        {selectedGrade && ` - Grade ${selectedGrade}`}
        {selectedTeacher && ` - Teacher ${selectedTeacher}`}
      </h3>
      
      <div className="grid grid-cols-7 gap-1 text-xs">
        {/* Header row */}
        <div className="font-montserrat font-semibold p-2 bg-northwood-navy text-white flex items-center justify-center text-base">Day/Period</div>
        {periods.map(period => (
          <div key={period} className="font-montserrat font-semibold p-2 bg-northwood-navy text-white flex items-center justify-center text-base">
            P{period}
          </div>
        ))}
        
        {/* Grid rows */}
        {days.map(day => (
          <React.Fragment key={day}>
            <div className="font-montserrat font-semibold p-2 bg-northwood-navy text-white flex items-center justify-center text-base">
              Day {day}
            </div>
            {periods.map(period => (
              <GridSlot
                key={`${day}-${period}`}
                day={day}
                period={period}
                slots={getSlotsByPosition(day, period)}
                onSlotUpdate={handleSlotUpdate}
                onLineDrop={onLineDrop}
              />
            ))}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

export default TimetableGrid;